import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {
  Status,
  type Reservation,
  type Reservee,
} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {
  ApprovalMode,
  type ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import type {Order} from '@wix/ambassador-ep-orders/types'
import type {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'

import {reservationLocationsService} from '../../services/reservationLocationsService'
import {reservationsService} from '../../services/reservationsService'
import {goToNewReservation} from '../../utils/navigation'
import {noop} from '../../utils/helpers'
import {getRegionalSettings} from '../../utils/flowAPI'
import {getFirstQueryParamValue} from '../../utils/queryParams'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {createStorageProvider} from '../../utils/storageContext'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {getReservationMock} from '../../editor/editorMocks/getReservationMock'
import {ApprovalTextEditorState, EXPERIMENTS} from '../../utils/constants'
import {checkoutService} from '../../services/checkoutService'
import {trackTableReservedEvent, trackPurchaseEvent} from '../../utils/trackEvent'
import {experiencesService} from '../../services/experiencesService'
import {
  cancellationFeeService,
  type CancellationFeePreviewResponse,
} from '../../services/cancellationFeeService'

interface ReservationConfirmationData {
  getReservationLocationsStatus: RequestStatus
  getReservationStatus: RequestStatus
  getOrderDetailsStatus: RequestStatus
  cancelReservationStatus: RequestStatus
  cancelReservationError?: any

  regionalSettings?: string
  reservationId: string | undefined
  reservationLocations: ReservationLocation[]
  reservation: Reservation | undefined
  orderId: string | undefined
  orderDetails: Order | undefined
  experience: Experience | undefined
  cancellationFeePreview?: CancellationFeePreviewResponse
  fitToContentHeight: boolean
  approvalTextEditorState: ApprovalTextEditorState.unknown
  isManualApproval: boolean
}

interface ReservationConfirmationActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  getReservation: (reservationId: string) => Promise<Reservation | undefined>
  getOrderDetails: (orderId: string) => Promise<Order | undefined>
  getExperience: (experienceId: string) => Promise<Experience | undefined>
  cancelReservation: (param: {
    reservationId: string
    revision: string
    phone?: string
  }) => Promise<Reservation | undefined>
  resetCancelReservationStatus: () => void
  goToNewReservation: (reservation?: Reservation, timeZone?: string | null) => void
  trackTableReservedEvent: (reservationId: string, reservee?: Reservee) => void
  trackPurchaseEvent: (reservationId: string, order: Order) => void
  previewCancellationFee: (
    reservationId: string,
  ) => Promise<CancellationFeePreviewResponse | undefined>
}

export interface ReservationConfirmationStorage
  extends ReservationConfirmationData,
    ReservationConfirmationActions {}

export const defaultReservationConfirmationStorage: ReservationConfirmationStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getReservationStatus: RequestStatus.DEFAULT,
  getOrderDetailsStatus: RequestStatus.DEFAULT,
  cancelReservationStatus: RequestStatus.DEFAULT,
  cancelReservationError: undefined,
  reservationId: undefined,
  reservation: undefined,
  orderId: undefined,
  orderDetails: undefined,
  reservationLocations: [],
  getReservationLocations: noop,
  getReservation: noop,
  getOrderDetails: noop,
  cancelReservation: noop,
  resetCancelReservationStatus: noop,
  goToNewReservation: noop,
  fitToContentHeight: true,
  approvalTextEditorState: ApprovalTextEditorState.unknown,
  isManualApproval: false,
  trackTableReservedEvent: noop,
  trackPurchaseEvent: noop,
  getExperience: noop,
  experience: undefined,
  cancellationFeePreview: undefined,
  previewCancellationFee: async () => ({applies: false}),
}

const {withStorageProvider, useStorage} = createStorageProvider(
  defaultReservationConfirmationStorage,
)

const initReservationConfirmationStorage = (
  flowAPI: ControllerFlowAPI,
): ReservationConfirmationStorage => {
  const query = flowAPI.controllerConfig?.wixCodeApi?.location?.query ?? {}
  const queryReservationId = getFirstQueryParamValue(query.reservationId) || undefined

  return {
    ...defaultReservationConfirmationStorage,
    regionalSettings: getRegionalSettings(flowAPI),
    reservationId: queryReservationId,
    orderId: queryReservationId ? getFirstQueryParamValue(query.orderId) : undefined,
    getReservationLocations: wrapRequest(
      flowAPI,
      reservationLocationsService.getReservationLocations,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getReservation: wrapRequest(
      flowAPI,
      reservationsService.getReservation,
      'reservation',
      'getReservationStatus',
    ),
    getOrderDetails: wrapRequest(
      flowAPI,
      checkoutService.getOrderDetails,
      'orderDetails',
      'getOrderDetailsStatus',
    ),
    getExperience: wrapRequest(
      flowAPI,
      experiencesService.getExperience,
      'experience',
      'getExperienceStatus',
    ),
    cancelReservation: wrapRequest(
      flowAPI,
      reservationsService.cancelReservation,
      'reservation',
      'cancelReservationStatus',
      'cancelReservationError',
    ),
    resetCancelReservationStatus: () => {
      flowAPI.controllerConfig.setProps({
        cancelReservationStatus: RequestStatus.DEFAULT,
        cancelReservationError: undefined,
      })
    },
    goToNewReservation: (reservation?: Reservation) =>
      goToNewReservation({
        flowAPI,
        reservationData: reservation?.details,
      }),
    trackTableReservedEvent: (reservationId: string, reservee?: Reservee) => {
      trackTableReservedEvent(flowAPI, reservationId, reservee)
    },
    trackPurchaseEvent: (reservationId: string, order: Order) => {
      trackPurchaseEvent(flowAPI, reservationId, order)
    },
    previewCancellationFee: flowAPI.experiments.enabled(
      EXPERIMENTS.tableReservationsCancellationFee,
    )
      ? wrapRequest(
          flowAPI,
          cancellationFeeService.previewCancellationFee,
          'cancellationFeePreview',
          'cancellationFeePreviewStatus',
        )
      : async () => ({applies: false}),
  }
}

const mockReservationConfirmationStorage = async (
  flowAPI: ControllerFlowAPI,
): Promise<ReservationConfirmationStorage> => {
  const reservationLocations = await getReservationLocationsMock(flowAPI)
  const reservation = getReservationMock(flowAPI, Status.RESERVED, reservationLocations[0].id!)

  const isManualApproval =
    reservationLocations[0].configuration?.onlineReservations?.approval?.mode ===
      ApprovalMode.MANUAL || false

  return {
    ...defaultReservationConfirmationStorage,
    reservationLocations,
    regionalSettings: getRegionalSettings(flowAPI),
    getReservationLocationsStatus: RequestStatus.RESOLVED,
    reservation,
    reservationId: reservation.id!,
    isManualApproval,
    cancelReservation: async () => {
      return {}
    },
    previewCancellationFee: async () => ({applies: false}),
  }
}

export {
  initReservationConfirmationStorage,
  mockReservationConfirmationStorage,
  withStorageProvider as withReservationConfirmationStorageProvider,
  useStorage as useReservationConfirmationStorage,
}
